import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { DeckInsertionPosition, DeckState, TabletopItemType } from '@/components/tabletop/types';
import CardBack from '../card/CardBack';
import TabletopItem from '@/components/tabletop/components/TabletopItem';

interface DeckProps {
  deck: DeckState;
  onDragStart?: (id: string, type: TabletopItemType, event: React.MouseEvent) => void;
  onSelect?: (id: string, type: TabletopItemType, event: ReactMouseEvent) => void;
  isSelected?: boolean;
  isHighlighted?: boolean;
  width?: number;
  height?: number;
  selectionColor?: string;
  highlightZone?: DeckInsertionPosition | null;
}

// Default card dimensions
const DEFAULT_CARD_WIDTH = 63;
const DEFAULT_CARD_HEIGHT = 88;
const STACKING_OFFSET = 0.2; // pixels between stacked cards
const MAX_VISIBLE_CARDS = 60; // maximum number of card edges to show in stack

const SHUFFLE_LAYERS = [
  { key: 'left', className: 'deck-shuffle-card deck-shuffle-card-left', delay: '0ms' },
  { key: 'center', className: 'deck-shuffle-card deck-shuffle-card-center', delay: '120ms' },
  { key: 'right', className: 'deck-shuffle-card deck-shuffle-card-right', delay: '240ms' }
];

// Component for rendering an empty deck
const EmptyDeck = ({ name }: { name: string }) => {
  return (
    <div className="relative flex h-full w-full items-center justify-center rounded-sm bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950">
      <div className="absolute inset-1 rounded-sm border border-white/8" />
      <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Empty</span>
      
      <div className="absolute bottom-0 left-0 right-0 bg-black/72 px-2 py-1 text-xs text-white truncate">
        {name}
      </div>
    </div>
  );
};

export default function Deck({
  deck,
  onDragStart,
  onSelect,
  isSelected = false,
  isHighlighted = false,
  width = DEFAULT_CARD_WIDTH,
  height = DEFAULT_CARD_HEIGHT,
  selectionColor,
  highlightZone = null
}: DeckProps) {
  const { id, position, rotation, cards, name, faceUp, collapsed } = deck;
  const [isShuffling, setIsShuffling] = useState(false);

  useEffect(() => {
    if (!deck.lastShuffledAt) {
      return;
    }

    setIsShuffling(true);

    const timeout = window.setTimeout(() => {
      setIsShuffling(false);
    }, 900);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [deck.lastShuffledAt]);
  
  // Get top card image (or default if deck is empty)
  const topCard = cards.length > 0 ? cards[cards.length - 1] : undefined;
  const topCardImage = topCard
    ? faceUp
      ? topCard.imageUrl
      : topCard.backImageUrl ?? topCard.imageUrl
    : undefined;

  const topCardName = cards.length > 0 ? cards[cards.length - 1].name : name;

  const shuffleCardStyle = useMemo(() => {
    const cardWidth = Math.max(width * 0.75, 40);
    const cardHeight = Math.max(height * 0.9, 56);

    const backgroundImage = topCardImage ? `url(${topCardImage})` : undefined;

    return {
      width: `${cardWidth}px`,
      height: `${cardHeight}px`,
      backgroundImage,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundColor: backgroundImage ? undefined : '#ffffff',
      backgroundRepeat: 'no-repeat'
    } as CSSProperties;
  }, [height, topCardImage, width]);

  // Handle selection - if the deck is already selected, draw a card
  const handleItemSelect = (deckId: string, type: TabletopItemType, event: ReactMouseEvent) => {
    if (onSelect) {
      onSelect(deckId, type, event);
    }
  };
  
  // Generate stack cards
  const renderStackedCards = () => {
    if (cards.length <= 1 || collapsed) return null;
    
    const stackedCards = [];
    // Show at most MAX_VISIBLE_CARDS edges
    const cardsToShow = Math.min(cards.length - 1, MAX_VISIBLE_CARDS);
    
    for (let i = 0; i < cardsToShow; i++) {
      stackedCards.push(
        <div 
          key={`stack-${i}`}
          className="absolute rounded-md overflow-hidden shadow-sm bg-white"
          style={{
            width: width,
            height: height,
            top: STACKING_OFFSET * (cardsToShow - i),
            left: STACKING_OFFSET * (cardsToShow - i),
            zIndex: -(cardsToShow - i),
          }}
        >
          {faceUp ? (
            <img
              src={cards[cards.length - 2 - i] ? cards[cards.length - 2 - i].imageUrl : topCardImage}
              alt="Card in stack"
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-gray-200">
              {topCardImage && (
                <img
                  src={topCardImage}
                  alt="Card back"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              )}
            </div>
          )}
        </div>
      );
    }
    
    return stackedCards;
  };
  
  return (
    <TabletopItem
      id={id}
      type="deck"
      position={position}
      rotation={rotation}
      width={width}
      height={height}
      isSelected={isSelected}
      isHighlighted={isHighlighted}
      onSelect={handleItemSelect}
      onDragStart={onDragStart}
      selectionColor={selectionColor}
    >
      {/* Stacked cards (underneath the top card) */}
      {renderStackedCards()}
      
      {/* Top card/main deck visual */}
      <div className="relative h-full w-full overflow-hidden rounded-md shadow-[0_12px_24px_rgba(15,23,42,0.22)]">
        {cards.length === 0 ? (
          /* Show custom empty deck UI when the deck has no cards */
          <EmptyDeck name={name} />
        ) : faceUp ? (
          /* Show top card face if deck is face up and has cards */
          <img
            src={cards[cards.length - 1].imageUrl}
            alt={topCardName}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          /* Show card back for face down decks with cards */
          <CardBack
            backImageUrl={topCardImage}
          />
        )}
        
        {/* Card count indicator */}
        <div className="absolute left-0 top-0 rounded-br-lg bg-black/72 px-2 py-1 text-[11px] font-semibold text-white">
          {cards.length}
        </div>
        
        {/* Highlight overlay for drag target indication */}
        {isHighlighted && (
          <div className="absolute inset-0 pointer-events-none z-10">
            {highlightZone && <div className="absolute inset-0 bg-green-500/20" />}
            {highlightZone === 'top' && (
              <div className="absolute inset-x-0 top-0 h-1/2 bg-green-500/40 backdrop-blur-[1px] border-b border-green-400/40 flex items-center justify-center">
                <span className="text-white text-[11px] font-semibold uppercase tracking-wide">
                  Place on Top
                </span>
              </div>
            )}
            {highlightZone === 'bottom' && (
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-green-500/40 backdrop-blur-[1px] border-t border-green-400/40 flex items-center justify-center">
                <span className="text-white text-[11px] font-semibold uppercase tracking-wide">
                  Place on Bottom
                </span>
              </div>
            )}
            {!highlightZone && (
              <div className="absolute inset-0 bg-green-500/25">
                <div className="absolute inset-x-0 top-0 bg-green-500/45 py-1 text-center text-white text-xs font-medium uppercase tracking-wide">
                  Add to Deck
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Deck name */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/72 px-2 py-1 text-xs font-medium text-white truncate">
          {name}
        </div>
      </div>

      {/* Shuffle overlay rendered above deck surface */}
      {isShuffling && (
        <div
          className="absolute inset-0 pointer-events-none z-40 overflow-visible"
          aria-hidden="true"
        >
          <div className="absolute inset-0 rounded-md bg-sky-500/30 blur-[6px]" />
          <div className="absolute inset-0 flex items-center justify-center overflow-visible">
            {SHUFFLE_LAYERS.map(layer => (
              <div
                key={layer.key}
                className={layer.className}
                style={{ ...shuffleCardStyle, animationDelay: layer.delay }}
              />
            ))}
          </div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-sky-700 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white shadow">
            Shuffling...
          </div>
        </div>
      )}

    </TabletopItem>
  );
}
